import React from "react";
import { GridFilterModel } from "@mui/x-data-grid-pro";
import {
  DateRangeFilter,
  GET_BUS_STATION_TRANSACTIONS,
  GET_PAYEE_TRANSACTIONS_PRICE_FILTER,
  GetBusStationTransactionsFilter,
  GetBusStationTransactionsVars,
  TransactionOrderBy,
} from "common/graphql/queries/GetCurbsuiteTransactions";
import { PAGE_SIZE } from "../useTransactionsUrlState";
import { BUS_STATION_TRANSACTION_TYPE_LABELS } from "../columns/busStationTransactionTypeLabels";
import { dateOperatorFilter, licensePlateFilter, operatorNameFilter, transactionIndexFilter } from "./filterHelpers";
import { TransactionsQueryResult, useTransactionsQuery } from "./useTransactionsQuery";

/**
 * GET_BUS_STATION_TRANSACTIONS wiring: filter builder plus the query/export-batch plumbing shared
 * with useCurbsuiteTransactionsData via useTransactionsQuery. Only bus-station-park uses this --
 * every other shape uses useCurbsuiteTransactionsData instead.
 */
export function useBusStationTransactionsData({
  appliedFilters,
  dateRangeFilter,
  orderBy,
  page,
  skip,
}: {
  appliedFilters: GridFilterModel | undefined;
  dateRangeFilter: DateRangeFilter;
  orderBy: TransactionOrderBy[];
  page: number;
  skip: boolean;
}): TransactionsQueryResult {
  const filter: GetBusStationTransactionsFilter = React.useMemo(() => {
    if (!appliedFilters) {
      return {};
    }

    const columnFilters = appliedFilters.items
      .map((item) => {
        if (item.columnField === "transactionIndex") {
          return transactionIndexFilter(item.value);
        }
        if (item.columnField === "busStationTransactionType" && item.value != null) {
          return {
            transaction_type: {
              _eq:
                item.value === BUS_STATION_TRANSACTION_TYPE_LABELS.park
                  ? ("park" as const)
                  : item.value === BUS_STATION_TRANSACTION_TYPE_LABELS.adjustment
                  ? ("adjustment" as const)
                  : ("gateway_transaction" as const),
            },
          };
        }
        if (item.columnField === "operator" && item.value != null) {
          return operatorNameFilter(item.value);
        }
        if (item.columnField === "transactionDate" || item.columnField === "transactionAssignedDate") {
          const graphQLField =
            item.columnField === "transactionDate" ? "transaction_date" : "assigned_to_account_at_local_date";
          return dateOperatorFilter(graphQLField, item.operatorValue, item.value);
        }
        if (item.columnField === "licensePlate") {
          return licensePlateFilter(item.operatorValue, item.value);
        }
        return undefined;
      })
      .filter(Boolean) as Record<string, unknown>[];

    if (appliedFilters.linkOperator === "and") {
      return { _and: columnFilters };
    } else {
      return { _or: columnFilters };
    }
  }, [appliedFilters]);

  const exportVariables = React.useMemo(
    () => ({
      orderBy,
      filter,
      dateRangeFilter,
      priceFilter: GET_PAYEE_TRANSACTIONS_PRICE_FILTER,
    }),
    [orderBy, filter, dateRangeFilter],
  );

  const variables: GetBusStationTransactionsVars = React.useMemo(
    () => ({ ...exportVariables, offset: (page - 1) * PAGE_SIZE, limit: PAGE_SIZE }),
    [exportVariables, page],
  );

  return useTransactionsQuery<GetBusStationTransactionsVars>(
    GET_BUS_STATION_TRANSACTIONS,
    variables,
    exportVariables,
    skip,
  );
}
